<template>
  <div class="candidate-dashboard flex flex-col justify-between min-h-screen bg-gray-50">
    <!-- Header -->
    <header class="flex justify-between items-center px-8 py-4 bg-white shadow-sm">
      <div class="flex items-center space-x-3">
        <span class="font-bold text-xl text-gray-800">考生个人中心</span>
      </div>
      <el-button type="text" @click="$router.push('/recruitment/portal')">返回校招首页</el-button>
    </header>

    <!-- Main Container -->
    <main class="flex-grow max-w-5xl w-full mx-auto px-6 py-12">
      <!-- 个人信息模块 -->
      <section class="profile-card bg-white p-6 rounded-2xl shadow-sm mb-8 flex items-center justify-between">
        <div class="flex items-center space-x-4">
          <div class="avatar-box w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-2xl">
            {{ candidateName ? candidateName.charAt(0) : 'S' }}
          </div>
          <div>
            <h2 class="text-xl font-bold text-gray-800">欢迎您，{{ candidateName }}</h2>
            <p class="text-gray-500 text-sm mt-1">您可在本中心查看已报名的活动及待参加的在线考试、性格测评任务。</p>
          </div>
        </div>
        <el-button type="danger" plain size="small" @click="handleLogout">退出登录</el-button>
      </section>

      <!-- 待办任务看板 -->
      <section class="tasks-section">
        <h3 class="text-lg font-bold text-gray-800 mb-6 flex items-center">
          <i class="el-icon-finished mr-2 text-blue-500"></i> 我的待办测试任务
        </h3>

        <div v-if="loading" class="loading-box text-center py-20">
          <el-skeleton :rows="6" animated />
        </div>

        <div v-else-if="testList.length === 0" class="empty-tasks bg-white rounded-2xl p-12 text-center shadow-sm">
          <img src="https://images.unsplash.com/photo-1590402421683-166852786b3c?auto=format&fit=crop&w=400&q=80" alt="No Tasks" class="w-48 h-32 mx-auto object-cover opacity-60 rounded-xl mb-4" />
          <p class="text-gray-400">目前没有待参加的考试或性格测评</p>
          <el-button type="primary" size="small" class="mt-4" @click="$router.push('/recruitment/portal')">去报名活动</el-button>
        </div>

        <div v-else class="grid grid-cols-2 gap-6">
          <div v-for="item in testList" :key="item.id" class="task-card bg-white p-6 rounded-2xl border border-gray-100 shadow-hover transition-all duration-300">
            <div class="flex justify-between items-start mb-4">
              <div>
                <span class="text-xs text-gray-400 font-medium block mb-1">{{ item.eventName }}</span>
                <h4 class="font-bold text-lg text-gray-800">{{ item.title }}</h4>
              </div>
              <el-tag :type="item.testType === 1 ? 'primary' : 'warning'" size="small">
                {{ item.testType === 1 ? '知识技能考试' : '性格特征测评' }}
              </el-tag>
            </div>

            <!-- 起止时间 -->
            <div class="text-gray-500 text-xs space-y-1 mb-6">
              <div><i class="el-icon-time mr-1"></i> 开始时间：{{ item.startTime }}</div>
              <div><i class="el-icon-time mr-1"></i> 截止时间：{{ item.endTime }}</div>
            </div>

            <!-- 下方动作区 -->
            <div class="flex justify-between items-center border-t pt-4">
              <!-- 考试成绩穿透展示 -->
              <div class="result-status">
                <span v-if="item.testType === 1 && item.status === 2" class="text-sm">
                  得分：<strong class="text-green-600 text-lg">{{ item.score }}</strong> 分
                  <el-tag size="mini" :type="item.pass === 1 ? 'success' : 'danger'" class="ml-1">
                    {{ item.pass === 1 ? '及格' : '不及格' }}
                  </el-tag>
                </span>
                <span v-else-if="item.testType === 2 && item.status === 2" class="text-green-600 font-semibold text-sm">
                  <i class="el-icon-circle-check mr-1"></i> 已完成
                </span>
                <span v-else-if="item.status === 1" class="text-blue-500 text-sm">
                  <i class="el-icon-loading mr-1"></i> 待阅卷/测评已提交
                </span>
                <span v-else-if="item.status === 0" class="text-orange-500 text-sm font-medium">
                  <i class="el-icon-warning-outline mr-1"></i> 待作答
                </span>
              </div>

              <!-- 开始测试按钮 (SSO 跳转) -->
              <el-button
                v-if="item.status === 0"
                type="primary"
                size="small"
                class="action-btn"
                @click="startTest(item)"
              >
                开始作答
              </el-button>
              <el-button v-else disabled size="small">已结束</el-button>
            </div>
          </div>
        </div>
      </section>
    </main>
  </div>
</template>

<script>
  import { getRecruitmentMyTestsApi } from '@/api';

  export default {
    name: 'RecruitmentDashboard',
    data() {
      return {
        loading: false,
        candidateName: localStorage.getItem('CandidateName') || '',
        testList: [],
        // NEO 测验端系统域名配置，这里在本地和开发环境下建议为 http://localhost:3100
        runnerBaseUrl: 'https://p.lietousaas.com/exam/', 
      };
    },
    mounted() {
      this.checkLogin();
      this.loadTests();
    },
    methods: {
      checkLogin() {
        const token = localStorage.getItem('Authorization');
        if (!token) {
          this.$message.warning('请先登录考生系统');
          this.$router.push('/recruitment/portal');
        }
      },
      async loadTests() {
        this.loading = true;
        try {
          const res = await getRecruitmentMyTestsApi();
          if (res && res.code === 0) {
            this.testList = res.data || [];
          } else {
            this.$message.error(res?.message || '获取测试任务列表失败');
          }
        } catch (e) {
          this.$message.error('加载任务列表失败，网络异常');
        } finally {
          this.loading = false;
        }
      },
      startTest(item) {
        if (!item.token) {
          this.$message.error('未生成合法的测验授信令牌');
          return;
        }
        // 使用单点登录（SSO）跳转逻辑，将 token 附加在 hash query 中
        const ssoUrl = `${this.runnerBaseUrl}#/candidate/login?token=${item.token}`;
        window.open(ssoUrl, '_blank');
      },
      handleLogout() {
        localStorage.removeItem('Authorization');
        localStorage.removeItem('CandidateName');
        this.$message.success('已退出登录');
        this.$router.push('/recruitment/portal');
      },
    },
  };
</script>

<style scoped>
  /* 基础布局样式 */
  .candidate-dashboard {
    background-color: #f8fafc;
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  }

  .profile-card {
    border-radius: 20px;
    border: none;
  }

  .avatar-box {
    box-shadow: 0 4px 10px rgba(24, 144, 255, 0.15);
  }

  .task-card {
    border-radius: 20px;
    border: none;
    transition: all 0.3s;
  }

  .shadow-hover:hover {
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.04);
    transform: translateY(-2px);
  }

  .action-btn {
    background: linear-gradient(135deg, #1890ff 0%, #4facfe 100%);
    border: none;
    font-weight: 600;
    border-radius: 8px;
  }

  /* Tailwind 兼容辅助类 */
  .flex { display: flex; }
  .flex-col { flex-direction: column; }
  .justify-between { justify-content: space-between; }
  .items-center { align-items: center; }
  .items-start { align-items: flex-start; }
  .flex-grow { flex-grow: 1; }
  .grid { display: grid; }
  .grid-cols-2 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .gap-6 { gap: 24px; }
  .space-x-3 > * + * { margin-left: 12px; }
  .space-x-4 > * + * { margin-left: 16px; }
  .space-y-1 > * + * { margin-top: 4px; }
  .px-8 { padding-left: 32px; padding-right: 32px; }
  .py-4 { padding-top: 16px; padding-bottom: 16px; }
  .px-6 { padding-left: 24px; padding-right: 24px; }
  .py-12 { padding-top: 48px; padding-bottom: 48px; }
  .py-20 { padding-top: 80px; padding-bottom: 80px; }
  .p-6 { padding: 24px; }
  .p-12 { padding: 48px; }
  .pt-4 { padding-top: 16px; }
  .bg-white { background-color: #ffffff; }
  .bg-blue-100 { background-color: #ebf8ff; }
  .shadow-sm { box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05); }
  .rounded-xl { border-radius: 12px; }
  .rounded-2xl { border-radius: 16px; }
  .rounded-full { border-radius: 9999px; }
  .border { border: 1px solid #edf2f7; }
  .border-gray-100 { border-color: #f7fafc; }
  .border-t { border-top: 1px solid #edf2f7; }
  .w-full { width: 100%; }
  .w-16 { width: 64px; }
  .h-16 { height: 64px; }
  .w-48 { width: 192px; }
  .h-32 { height: 128px; }
  .max-w-5xl { max-width: 1024px; }
  .mx-auto { margin-left: auto; margin-right: auto; }
  .font-bold { font-weight: 700; }
  .font-medium { font-weight: 500; }
  .font-semibold { font-weight: 600; }
  .text-xs { font-size: 12px; }
  .text-sm { font-size: 14px; }
  .text-lg { font-size: 18px; }
  .text-xl { font-size: 20px; }
  .text-2xl { font-size: 24px; }
  .text-gray-800 { color: #1a202c; }
  .text-gray-500 { color: #718096; }
  .text-gray-400 { color: #cbd5e0; }
  .text-blue-500 { color: #4299e1; }
  .text-blue-600 { color: #3182ce; }
  .text-green-600 { color: #38a169; }
  .text-orange-500 { color: #dd6b20; }
  .text-center { text-align: center; }
  .block { display: block; }
  .mb-1 { margin-bottom: 4px; }
  .mb-4 { margin-bottom: 16px; }
  .mb-6 { margin-bottom: 24px; }
  .mb-8 { margin-bottom: 32px; }
  .mt-1 { margin-top: 4px; }
  .mt-4 { margin-top: 16px; }
  .mr-1 { margin-right: 4px; }
  .mr-2 { margin-right: 8px; }
  .ml-1 { margin-left: 4px; }
  .opacity-60 { opacity: 0.6; }
  .object-cover { object-fit: cover; }
</style>
